/*!

=========================================================
* Argon Dashboard React - v1.2.4
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
/*eslint-disable*/
import { useCallback, useContext, useState } from "react";
import { NavLink as NavLinkRRD, Link, useLocation, useNavigate } from "react-router-dom";
// nodejs library to set properties for components
import { PropTypes } from "prop-types";

// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Collapse,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  DropdownToggle,
  FormGroup,
  Form,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Media,
  NavbarBrand,
  Navbar,
  NavItem,
  NavLink,
  Nav,
  Progress,
  Table,
  Container,
  Row,
  Col,
} from "reactstrap";
import { config } from "config";
import AppContext from "AppContext";
import { PapelEnum } from "services/PerfilService";
import { TipoContratoEnum } from "services/ContratoService";

var ps;

const Sidebar = (props) => {
  const [collapseOpen, setCollapseOpen] = useState();
  const [gruposAbertos, setGruposAbertos] = useState({});
  const { state, actions } = useContext(AppContext)
  const navigate = useNavigate()
  const location = useLocation()


  // verifies if routeName is the one active (in browser input)
  const activeRoute = (routeName) => {
    return props.location.pathname.indexOf(routeName) > -1 ? "active" : "";
  };
  // toggles collapse between opened and closed (true/false)
  const toggleCollapse = () => {
    setCollapseOpen((data) => !data);
  };
  // closes the collapse
  const closeCollapse = () => {
    setCollapseOpen(false);
  };
  // abre/fecha um grupo de itens (ex.: "Cadastros auxiliares") dentro da sidebar -- recebe o
  // estado efetivo atual, já que um grupo nunca clicado segue a rota atual (ver createLinks)
  const toggleGrupo = (grupo, aberto) => {
    setGruposAbertos((data) => ({ ...data, [grupo]: !aberto }));
  };

  const renderLink = (prop, key) => (
    <NavItem key={key}>
      <NavLink
        to={prop.layout + prop.path}
        tag={NavLinkRRD}
        onClick={closeCollapse}
      >
        {prop.name}
      </NavLink>
    </NavItem>
  );

  const createLinks = useCallback((routes) => {

    const idTipoPerfil = state?.user?.perfil?.idTipo
    if (!idTipoPerfil)
      return null

    let fn = () => true
    if (idTipoPerfil != PapelEnum.ADMIN) {
      const contrato = state?.contrato
      // SDD 046 - sem contrato selecionado, so as telas que nao dependem de contrato
      // (escopo por empresa: equipes, veiculos, usuarios)
      if (!contrato)
        fn = (route) => !!route.independeContrato
      else if (contrato.tipoContrato == TipoContratoEnum.NORMAL)
        fn = (route) => !route.ocultarContratoNormal
    }

    const visiveis = routes.filter(fn)
    const gruposRenderizados = new Set()

    return visiveis.map((prop, key) => {
      if (prop.grupo) {
        // já renderizado junto com o primeiro item deste grupo -- o grupo inteiro (cabeçalho +
        // itens) some sozinho quando nenhum item dele sobra em `visiveis` (ex.: perfil sem
        // permissão pra nenhum dos cadastros do grupo), já que este bloco nunca roda nesse caso
        if (gruposRenderizados.has(prop.grupo))
          return null

        gruposRenderizados.add(prop.grupo)
        const itensGrupo = visiveis.filter((r) => r.grupo === prop.grupo)

        // grupo com 1 só item visível pro perfil vira item solto, sem cabeçalho
        if (itensGrupo.length === 1)
          return renderLink(itensGrupo[0], key)

        // sem clique do usuário, o grupo da tela atual começa aberto; depois do clique, vale o clique
        const contemRotaAtual = itensGrupo.some((r) => location.pathname.startsWith(r.layout + r.path))
        const aberto = gruposAbertos[prop.grupo] ?? contemRotaAtual

        return (
          <NavItem key={key}>
            <NavLink
              href="#"
              onClick={(ev) => { ev.preventDefault(); toggleGrupo(prop.grupo, aberto) }}
              className="d-flex justify-content-between align-items-center"
            >
              <span>{prop.grupo}</span>
              <i className={aberto ? "bi bi-chevron-up" : "bi bi-chevron-down"} />
            </NavLink>
            <Collapse isOpen={aberto}>
              <Nav className="nav-sm flex-column" navbar style={{ marginLeft: "1rem", marginRight: 0 }}>
                {itensGrupo.map((item, subKey) => (
                  <NavItem key={subKey}>
                    <NavLink
                      to={item.layout + item.path}
                      tag={NavLinkRRD}
                      onClick={closeCollapse}
                    >
                      {item.name}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
            </Collapse>
          </NavItem>
        )
      }

      return renderLink(prop, key)
    });
  }, [state, gruposAbertos, location.pathname])

  const { bgColor, routes, logo } = props;
  let navbarBrandProps;
  if (logo && logo.innerLink) {
    navbarBrandProps = {
      to: logo.innerLink,
      tag: Link,
    };
  } else if (logo && logo.outterLink) {
    navbarBrandProps = {
      href: logo.outterLink,
      target: "_blank",
    };
  }

  const handleLogout = (ev) => {
    ev.preventDefault()
    actions.setUser(null)
    navigate('/auth/login')
  }

  return (
    <Navbar
      className="navbar-vertical fixed-left navbar-light bg-white"
      expand="md"
      id="sidenav-main"
    >
      <Container fluid>
        {/* Toggler */}
        <button
          className="navbar-toggler"
          type="button"
          onClick={toggleCollapse}
        >
          <span className="navbar-toggler-icon" />
        </button>
        {/* Brand */}
        {logo ? (
          <NavbarBrand className="pt-0" {...navbarBrandProps}>
            <img
              alt={logo.imgAlt}
              className="navbar-brand-img"
              src={logo.imgSrc}
            />
          </NavbarBrand>
        ) : null}
        {/* User */}
        <Nav className="align-items-center d-md-none">
          {/* <UncontrolledDropdown nav>
            <DropdownToggle nav className="nav-link-icon">
              <i className="ni ni-bell-55" />
            </DropdownToggle>
            <DropdownMenu
              aria-labelledby="navbar-default_dropdown_1"
              className="dropdown-menu-arrow"
              right
            >
              <DropdownItem>Action</DropdownItem>
              <DropdownItem>Another action</DropdownItem>
              <DropdownItem divider />
              <DropdownItem>Something else here</DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown> */}
          <UncontrolledDropdown nav>
            <DropdownToggle nav>
              <Media className="align-items-center">
                <span className="avatar avatar-sm rounded-circle">
                  <img
                    alt="..."
                    src={require("../../assets/img/theme/team-4-800x800.jpg")}
                  />
                </span>
              </Media>
            </DropdownToggle>
            <DropdownMenu className="dropdown-menu-arrow" right>
              {/* <DropdownItem className="noti-title" header tag="div">
                <h6 className="text-overflow m-0">Welcome!</h6>
              </DropdownItem>
              <DropdownItem to="/admin/user-profile" tag={Link}>
                <i className="ni ni-single-02" />
                <span>My profile</span>
              </DropdownItem>
              <DropdownItem to="/admin/user-profile" tag={Link}>
                <i className="ni ni-settings-gear-65" />
                <span>Settings</span>
              </DropdownItem>
              <DropdownItem to="/admin/user-profile" tag={Link}>
                <i className="ni ni-calendar-grid-58" />
                <span>Activity</span>
              </DropdownItem> 
              <DropdownItem to="/admin/user-profile" tag={Link}>
                <i className="ni ni-support-16" />
                <span>Support</span>
              </DropdownItem>
              <DropdownItem divider />*/}
              <DropdownItem href="#pablo" onClick={handleLogout}>
                <i className="ni ni-user-run" />
                <span>Sair</span>
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </Nav>
        {/* Collapse */}
        <Collapse navbar isOpen={collapseOpen}>
          {/* Collapse header */}
          <div className="navbar-collapse-header d-md-none">
            <Row>
              {logo ? (
                <Col className="collapse-brand" xs="6">
                  {logo.innerLink ? (
                    <Link to={logo.innerLink}>
                      <img alt={logo.imgAlt} src={logo.imgSrc} />
                    </Link>
                  ) : (
                    <a href={logo.outterLink}>
                      <img alt={logo.imgAlt} src={logo.imgSrc} />
                    </a>
                  )}
                </Col>
              ) : null}
              <Col className="collapse-close" xs="6">
                <button
                  className="navbar-toggler"
                  type="button"
                  onClick={toggleCollapse}
                >
                  <span />
                  <span />
                </button>
              </Col>
            </Row>
          </div>
          {/* Form */}
          {/* <Form className="mt-4 mb-3 d-md-none">
            <InputGroup className="input-group-rounded input-group-merge">
              <Input
                aria-label="Search"
                className="form-control-rounded form-control-prepended"
                placeholder="Search"
                type="search"
              />
              <InputGroupAddon addonType="prepend">
                <InputGroupText>
                  <span className="fa fa-search" />
                </InputGroupText>
              </InputGroupAddon>
            </InputGroup>
          </Form> */}
          {/* Navigation */}
          <Nav navbar>{createLinks(routes)}</Nav>

          {config.showMenu && (
            <>
              {/* Divider */}
              <hr className="my-3" />
              {/* Heading */}
              <h6 className="navbar-heading text-muted">Documentation</h6>
              {/* Navigation */}
              <Nav className="mb-md-3" navbar>
                <NavItem>
                  <NavLink href="https://demos.creative-tim.com/argon-dashboard-react/#/documentation/overview?ref=adr-admin-sidebar">
                    <i className="ni ni-spaceship" />
                    Getting started
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink href="https://demos.creative-tim.com/argon-dashboard-react/#/documentation/colors?ref=adr-admin-sidebar">
                    <i className="ni ni-palette" />
                    Foundation
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink href="https://demos.creative-tim.com/argon-dashboard-react/#/documentation/alerts?ref=adr-admin-sidebar">
                    <i className="ni ni-ui-04" />
                    Components
                  </NavLink>
                </NavItem>
              </Nav>
            </>
          )}


          {/* <Nav className="mb-md-3" navbar>
            <NavItem className="active-pro active">
              <NavLink href="https://www.creative-tim.com/product/argon-dashboard-pro-react?ref=adr-admin-sidebar">
                <i className="ni ni-spaceship" />
                Upgrade to PRO
              </NavLink>
            </NavItem>
          </Nav> */}
        </Collapse>
      </Container>
    </Navbar>
  );
};

Sidebar.defaultProps = {
  routes: [{}],
};

Sidebar.propTypes = {
  // links that will be displayed inside the component
  routes: PropTypes.arrayOf(PropTypes.object),
  logo: PropTypes.shape({
    // innerLink is for links that will direct the user within the app
    // it will be rendered as <Link to="...">...</Link> tag
    innerLink: PropTypes.string,
    // outterLink is for links that will direct the user outside the app
    // it will be rendered as simple <a href="...">...</a> tag
    outterLink: PropTypes.string,
    // the image src of the logo
    imgSrc: PropTypes.string.isRequired,
    // the alt for the img
    imgAlt: PropTypes.string.isRequired,
  }),
};

export default Sidebar;
